/**
 * The desktop shell's settings, read and written over the bridge.
 *
 * The shell is the source of truth: `save` resolves with what the shell
 * actually saved, and a change made from the tray arrives through
 * `onChange`. A failure is surfaced in `error` and logged, never swallowed.
 */

import { useCallback, useEffect, useState } from "react";

import { logError } from "utility/Function";

import type {
  DesktopSettings,
  DesktopSettingsPatch,
  WoneDesktopBridge,
} from "../bridge";

export type DesktopSettingsBridge = NonNullable<WoneDesktopBridge["settings"]>;

export interface DesktopSettingsState {
  /** Null until the first read resolves, and after it fails. */
  settings: DesktopSettings | null;
  error: string | null;
  saving: boolean;
  /** True when the shell saved the patch. */
  save(patch: DesktopSettingsPatch): Promise<boolean>;
}

function messageOf(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

export function useDesktopSettings(
  bridge: DesktopSettingsBridge | null
): DesktopSettingsState {
  const [settings, setSettings] = useState<DesktopSettings | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (bridge === null) return undefined;
    let active = true;
    const unsubscribe = bridge.onChange((next) => {
      if (active) setSettings(next);
    });
    bridge
      .get()
      .then((current) => {
        if (active) setSettings(current);
      })
      .catch((caught: unknown) => {
        logError("Could not read the desktop settings", caught);
        if (active) setError(messageOf(caught));
      });
    return () => {
      active = false;
      unsubscribe();
    };
  }, [bridge]);

  const save = useCallback(
    async (patch: DesktopSettingsPatch): Promise<boolean> => {
      if (bridge === null) {
        throw new Error(
          "Desktop settings are only available in the desktop app"
        );
      }
      setSaving(true);
      try {
        setSettings(await bridge.update(patch));
        setError(null);
        return true;
      } catch (caught) {
        logError("Could not save the desktop settings", caught);
        setError(messageOf(caught));
        return false;
      } finally {
        setSaving(false);
      }
    },
    [bridge]
  );

  return { settings, error, saving, save };
}
